import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, Heart, LayoutDashboard, Shield, LogOut, MapPin, ChevronDown, TrendingUp, Wind, Droplets, Key, Home as HomeIcon, HardHat, Zap, Trees, Paintbrush, Warehouse, Bug, Phone } from "lucide-react";

const GROW_LINKS = [
  { to: "/grow-my-business", label: "More Leads & Sales", Icon: TrendingUp },
  { to: "/hvac", label: "HVAC Companies", Icon: Wind },
  { to: "/plumbers", label: "Plumbers", Icon: Droplets },
  { to: "/roofers", label: "Roofers", Icon: HardHat },
  { to: "/electricians", label: "Electricians", Icon: Zap },
  { to: "/landscapers", label: "Landscapers", Icon: Trees },
  { to: "/painters", label: "Painters", Icon: Paintbrush },
  { to: "/garage-door", label: "Garage Door", Icon: Warehouse },
  { to: "/pest-control", label: "Pest Control", Icon: Bug },
  { to: "/buy-a-home", label: "Buy a Home", Icon: Key },
  { to: "/sell-your-home", label: "Sell Your Home", Icon: HomeIcon },
];

const LOGO = "/sam-logo-full.png";

export default function Header() {
  const { user, openAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState(false);

  const nav = [
    { to: "/browse", label: "Browse" },
    { to: "/postcards", label: "Postcard Mailers" },
    { to: "/plans", label: "Plans & Pricing" },
    { to: "/about", label: "About" },
    { to: "/careers", label: "Careers" },
    { to: "/podcast", label: "Podcast" },
    ...(user && (["admin", "staff"].includes(user.role) || user.referly_code) ? [{ to: "/prospector", label: "Prospector" }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-sam-cream/85 border-b border-border">
      <div className="sam-flag-bar h-1 w-full" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center group" data-testid="logo-home">
          <img src={LOGO} alt="Saving America Money" className="h-11 w-auto object-contain" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="px-3 py-2 rounded-md text-sm font-semibold text-sam-slate hover:text-sam-navy hover:bg-sam-navy/5 transition-colors">
              {n.label}
            </Link>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-grow-trigger"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-semibold text-sam-slate hover:text-sam-navy hover:bg-sam-navy/5 transition-colors outline-none">
                Grow Your Business <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {GROW_LINKS.map((g) => (
                <DropdownMenuItem key={g.to} onClick={() => navigate(g.to)} data-testid={`nav-grow-${g.to.replace(/\//g, "")}`}>
                  <g.Icon className="h-4 w-4 mr-2 text-sam-crimson" />{g.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className="flex items-center gap-2">
          <a href="tel:+18085592600" className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-sam-crimson/40 px-3.5 py-1.5 text-sm font-bold text-sam-crimson hover:bg-sam-crimson hover:text-white transition-colors" data-testid="header-phone"><Phone className="h-4 w-4" />(808) 559-2600</a>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-sam-navy/5 transition" data-testid="user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-sam-navy text-sam-cream text-xs">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-semibold text-sam-navy max-w-[110px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-xs text-muted-foreground truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="h-4 w-4 mr-2" />My Dashboard</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favorites")} data-testid="menu-favorites"><Heart className="h-4 w-4 mr-2" />Saved</DropdownMenuItem>
                {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><Shield className="h-4 w-4 mr-2" />Admin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" className="hidden sm:inline-flex text-sam-navy font-semibold" onClick={() => openAuth("shopper")} data-testid="header-login">Log In</Button>
              <Button className="bg-sam-crimson hover:bg-sam-crimson/90 font-semibold" onClick={() => openAuth("owner")} data-testid="header-list-business">List Your Business</Button>
            </>
          )}
          <button className="lg:hidden p-2" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button>
        </div>
      </div>
      {mobile && (
        <div className="lg:hidden border-t border-border bg-sam-cream px-4 py-2">
          {nav.map((n) => (
            <Link key={n.label} to={n.to} onClick={() => setMobile(false)} className="flex items-center gap-2 py-2.5 text-sm font-semibold text-sam-navy">
              <MapPin className="h-4 w-4 text-sam-crimson" />{n.label}
            </Link>
          ))}
          <div className="mt-1 pt-2 border-t border-border text-[10px] font-bold uppercase tracking-widest text-sam-slate">Grow Your Business</div>
          {GROW_LINKS.map((g) => (
            <Link key={g.to} to={g.to} onClick={() => setMobile(false)} data-testid={`mobile-grow-${g.to.replace(/\//g, "")}`}
              className="flex items-center gap-2 py-2.5 text-sm font-semibold text-sam-navy">
              <g.Icon className="h-4 w-4 text-sam-crimson" />{g.label}
            </Link>
          ))}
          <a href="tel:+18085592600" onClick={() => setMobile(false)} className="mt-3 flex items-center justify-center gap-2 rounded-full bg-sam-crimson text-white py-2.5 text-sm font-bold" data-testid="mobile-phone"><Phone className="h-4 w-4" />Call or text (808) 559-2600</a>
        </div>
      )}
    </header>
  );
}
